const base = (process.env.REACT_APP_API_BASE_URL || "/api").replace(/\/$/, "");
let accessTokenProvider = async () => null;

// The API is the authority on whether a Keycloak identity maps to an active
// DriverOS account.  Let the auth boundary react before individual screens
// render their partial error states.
export const AUTH_INVALID_EVENT = "driveros:auth-invalid";
function notifyInvalidAuthentication() {
  window.dispatchEvent(new Event(AUTH_INVALID_EVENT));
}

export function setAccessTokenProvider(provider) {
  accessTokenProvider = provider || (async () => null);
}

async function requestHeaders(json = false) {
  const token = await accessTokenProvider();
  return { Accept: "application/json", ...(json ? { "Content-Type": "application/json" } : {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export const DATA_CHANGED = "driveros:data-changed";
function notifyDataChanged() {
  window.dispatchEvent(new Event(DATA_CHANGED));
  try { localStorage.setItem(DATA_CHANGED, `${Date.now()}-${Math.random()}`); } catch (_) { /* Storage can be unavailable in private mode. */ }
}

async function get(path, params = {}) {
  const search = new URLSearchParams(Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== ""));
  const response = await fetch(`${base}${path}${search.toString() ? `?${search}` : ""}`, { headers: await requestHeaders() });
  if (!response.ok) {
    if (response.status === 401) notifyInvalidAuthentication();
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Request failed (${response.status})`);
  }
  return response.json();
}

async function post(path, body) {
  const response = await fetch(`${base}${path}`, { method: "POST", headers: await requestHeaders(true), body: JSON.stringify(body) });
  if (!response.ok) {
    if (response.status === 401) notifyInvalidAuthentication();
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Request failed (${response.status})`);
  }
  const result = await response.json();
  notifyDataChanged();
  return result;
}

// Some POST endpoints are computational reads.  They must not invalidate the
// whole operational UI as a mutation would.
async function postRead(path, body) {
  const response = await fetch(`${base}${path}`, { method: "POST", headers: await requestHeaders(true), body: JSON.stringify(body) });
  if (!response.ok) {
    if (response.status === 401) notifyInvalidAuthentication();
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Request failed (${response.status})`);
  }
  return response.json();
}

async function patch(path, body) {
  const response = await fetch(`${base}${path}`, { method: "PATCH", headers: await requestHeaders(true), body: JSON.stringify(body) });
  if (!response.ok) {
    if (response.status === 401) notifyInvalidAuthentication();
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Request failed (${response.status})`);
  }
  const result = await response.json();
  notifyDataChanged();
  return result;
}

async function del(path) {
  const response = await fetch(`${base}${path}`, { method: "DELETE", headers: await requestHeaders() });
  if (!response.ok) {
    if (response.status === 401) notifyInvalidAuthentication();
    const error = await response.json().catch(() => ({}));
    throw new Error(error.detail || `Request failed (${response.status})`);
  }
  const result = await response.json();
  notifyDataChanged();
  return result;
}

export const api = {
  me: () => get("/me"),
  driverTransfers: (start, end) => get("/driver/transfers", { start, end }),
  driverAvailability: (start, end) => get("/driver/availability", { start, end }),
  setDriverAvailability: (data) => post("/driver/availability", data),
  clientProjects: (start, end) => get("/client/projects", { start, end }),
  clientBookingRequests: () => get("/client/booking-requests"),
  createClientBookingRequest: (data) => post("/client/booking-requests", data),
  bookingRequests: (status) => get("/booking-requests", { status }),
  bookingRequest: (requestId) => get(`/booking-requests/${requestId}`),
  updateBookingRequest: (requestId, data) => patch(`/booking-requests/${requestId}`, data),
  approveBookingRequest: (requestId, data = {}) => post(`/booking-requests/${requestId}/approve`, data),
  clientProfile: () => get("/client/profile"),
  updateClientProfile: (data) => patch("/client/profile", data),
  notifications: (page = 1) => get("/notifications", { page, page_size: 20 }),
  markNotificationRead: (notificationId) => patch(`/notifications/${notificationId}/read`, {}),
  markAllNotificationsRead: () => post("/notifications/read-all", {}),
  health: () => get("/health"), dashboard: (date) => get("/dashboard", { date }),
  dayByDay: (date) => get("/day-by-day", { date }),
  fleetTimeline: (date) => get("/fleet-timeline", { date }),
  calendar: (start, end) => get("/calendar", { start, end }), cars: (q, status = "active") => get("/cars", { q, status }),
  statistics: (start, end) => get("/statistics", { start, end }),
  statisticsHistory: () => get("/statistics/history"),
  auditLog: (entity_type, action, page = 1) => get("/audit-log", { entity_type, action, page, page_size: 50 }),
  organizations: (q, page = 1) => get("/organizations", { q, page, page_size: 50 }),
  organization: (organizationId) => get(`/organizations/${organizationId}`),
  createOrganization: (data) => post("/organizations", data),
  updateOrganization: (organizationId, data) => patch(`/organizations/${organizationId}`, data),
  assignOrganizationClient: (organizationId, accountId) => post(`/organizations/${organizationId}/clients`, { account_id: accountId }),
  accounts: (q, role, status, sort, page = 1) => get("/accounts", { q, role, status, sort, page, page_size: 50 }), availability: (start, end) => get("/availability", { start, end }),
  setAvailability: (data) => post("/availability", data),
  assignSuggestedDriver: (transferId, driverId) => post(`/transfers/${transferId}/assign-driver`, { driver_id: driverId }),
  createAccount: (data) => post("/accounts", data),
  sendAccountSetupEmail: (accountId) => post(`/accounts/${accountId}/send-setup-email`, {}),
  updateAccountRole: (accountId, security_level) => patch(`/accounts/${accountId}/role`, { security_level }),
  publicRegister: (data) => post("/public/registrations", data),
  transfers: (q, start, end, page = 1, sort = "created") => get("/transfers", { q, start, end, page, page_size: 50, sort }),
  projects: (q, page = 1) => get("/projects", { q, page, page_size: 50 }), project: (projectId, start, end) => get(`/projects/${projectId}`, { start, end }),
  projectOptions: () => get("/project-options"),
  projectClients: (q, selectedId, limit = 10) => get("/project-clients", { q, selected_id: selectedId, limit }),
  geocode: (q, lang) => get("/geocode", { q, lang }),
  roadRoute: (points) => postRead("/routes/road", { points }),
  hauls: (transferId) => get(`/transfers/${transferId}/hauls`),
  haulOptions: () => get("/haul-options"), createHaul: (transferId, data) => post(`/transfers/${transferId}/hauls`, data),
  updateHaul: (haulId, data) => patch(`/hauls/${haulId}`, data), deleteHaul: (haulId) => del(`/hauls/${haulId}`),
  createProject: (data) => post("/projects", data), updateProject: (projectId, data) => patch(`/projects/${projectId}`, data), deleteProject: (projectId) => del(`/projects/${projectId}`),
  createTransfer: (projectId, data) => post(`/projects/${projectId}/transfers`, data),
  createRoute: (projectId, data) => post(`/projects/${projectId}/routes`, data),
  updateTransfer: (transferId, data) => patch(`/transfers/${transferId}`, data), deleteTransfer: (transferId) => del(`/transfers/${transferId}`),
  createCar: (data) => post("/cars", data), updateCar: (carId, data) => patch(`/cars/${carId}`, data),
  updateCarColor: (carId, color) => patch(`/cars/${carId}/color`, { color }),
  assistantChat: (question) => post("/assistant/chat", { question }),
  assistantActionPreview: (action, data) => post("/assistant/actions/preview", { action, data }),
  assistantActionConfirm: (confirmation_token) => post("/assistant/actions/confirm", { confirmation_token, confirm: true }),
};
